import axios from 'axios'
import merge from 'lodash/merge'
import constants from 'constants'
import { getConfig } from 'utils/config'
// -----------------
// API config init
// -----------------
export const init = () => {
  // Allows API requests to use windows auth
  axios.defaults.baseURL = getConfig().API_BASE
  axios.defaults.withCredentials = true
  axios.interceptors.response.use(async (response) => {
    try {
      return response
    } catch (error) {
      return Promise.reject(error)
    }
  })
}

// -----------------
// API common calls
// -----------------
const requestBody = (employeeId, selectedEmployeeId = '', userName = '', folderName = '') => {
  const api = {
    employee_id: employeeId,
    selected_employee_id: selectedEmployeeId,
    loginname: userName,
    folder_name: folderName,
  }
  return (api)
}

const responseBody = function (response) { return response.data }
const request = {
  get(url) { return axios.get(url).then(responseBody) },
  post(url, body) { return axios.post(url, body).then(responseBody) },
  postDoc(url, body, cancelToken) {
    return axios.post(url, body, {
      cancelToken,
    }).then(responseBody)
  },
  put(url, body) { return axios.put(url, body).then(responseBody) },
  del(url) { return axios.delete(url).then(responseBody) },
}

const PersonalInfo = {
  list(status, employeeid) { return request.post(`/personaldata/status/${status}`, requestBody(employeeid)) },
  details(employeeid) { return request.post('/personaldata/partnerdata', requestBody(employeeid)) },
  // create: function (personaldata) { return axios.post("/personaldata", personaldata); },
  update(personaldata) { return request.put('/personaldata', personaldata) },
  // delete: function (id) { return axios.delete("/personaldata/" + id); }
}

const Documents = {
  // getDocuments(libraryName, folderName, cancelToken) { return request.postDoc(`documents/getdocuments/${libraryName}`, requestBody('', '', '', folderName), cancelToken) },
  getDocuments(libraryName, folderName, employeeid, username, cancelToken) {
    return request.postDoc(`documents/getdocuments/${libraryName}`, requestBody(employeeid, '', username, folderName), cancelToken)
  },
  deleteDocument(itemid, libraryName, folderName, employeeid, username, cancelToken) {
    return request.postDoc(`documents/deletedocument/${itemid}/${libraryName}`, requestBody(employeeid, '', username, folderName), cancelToken)
  },
}

const PageHTMLContents = {
  getPageHTMLContents(libraryName, folderName, employeeid) { return request.post(`documents/getpagecontent/${libraryName}`, requestBody(employeeid, '', '', folderName)) },
}

const Reports = {
  getReports(employeeid) { return request.post('reports', requestBody(employeeid)) },
}

const UploadLogs = {
  getUploadLogs(libraryName, folderName) { return request.get(`documents/getuploadlogs/${libraryName}/${folderName}`) },
}

const PartnerSideBar = {
  details(employeeid) { return request.post('/partnersidebar', requestBody(employeeid)) },
}

const Beneficiary = {
  list(employeeid) { return request.post('/beneficiary/lists', requestBody(employeeid)) },
  listall(employeeid) { return request.post('/beneficiary/listsall', requestBody(employeeid)) },
  details(id, employeeid) { return request.post(`/beneficiary/get/${id}`, requestBody(employeeid)) },
  create(beneficiarydata) { return request.post('/beneficiary', beneficiarydata) },
  update(beneficiarydata) { return request.put(`/beneficiary/${beneficiarydata.beneficiary_id}`, beneficiarydata) },
  delete(beneficiarydata) { return request.post(`/beneficiary/delete/${beneficiarydata.beneficiary_id}`, requestBody(beneficiarydata.employee_id)) },
  notes(year, employeeid) { return request.post(`/beneficiary/${year}`, requestBody(employeeid)) },
  updatenotes(notes) { return request.put('/beneficiary/notes', notes) },
  confirm(beneficiarydata) { return request.put('/beneficiary/confirm', beneficiarydata) },
  lockandrem(employeeid) { return request.post('/beneficiary/lockstatusandrem', requestBody(employeeid)) },
  lock(employeeid, islocked) { return request.post(`/beneficiary/lock/${islocked}`, requestBody(employeeid)) },
  getlockstatus(employeeid) { return request.post('/beneficiary/getlockstatus', requestBody(employeeid)) },
  getuploads() { return request.get(`/documents/getbeneficiaryuploads/${getConfig().BENEFICIARY_UPLOAD}`) },
}

const AccountsAndLoans = {
  list(accounttype, employeeid) { return request.post(`/accountsandloans/${accounttype}`, requestBody(employeeid)) },
  details(accounttype, accountno, employeeid) { return request.post(`/accountsandloans/${accounttype}/${accountno}`, requestBody(employeeid)) },
  create(banktransferdata) { return request.post('/accountsandloans/banktransfer', banktransferdata) },
  listbanktransferaccount(employeeid) { return request.post('/accountsandloans/banktransferaccounts', requestBody(employeeid)) },
  listlockedaccount(employeeid) { return request.post('/accountsandloans/lockedaccount/lists', requestBody(employeeid)) },
  createlockedaccount(lockedaccountdata) { return request.post('/accountsandloans/lockedaccount', lockedaccountdata) },
  deletelockedaccount(lockedaccountdata) { return request.post(`/accountsandloans/lockedaccount/delete/${lockedaccountdata.accountprefix}`, requestBody(lockedaccountdata.employee_id)) },
  listbanktransfers(employeeid) { return request.post('/accountsandloans/banktransfers', requestBody(employeeid)) },
  savedbankaccounts(employeeid) { return request.post('/accountsandloans/savedbankaccounts', requestBody(employeeid)) },
}

const BankDetail = {
  details(bankBSB, employeeid) { return request.post(`/accountsandloans/bankaccount/${bankBSB}`, requestBody(employeeid)) },
}

const PartnerPlans = {
  list(viewtype, employeeid, planyear) { return request.post(`/partnerplan/${viewtype}/${planyear}`, requestBody(employeeid)) },
  details(employeeid, foremployeeid, planyear) { return request.post(`/partnerplan/${planyear}/plan`, requestBody(employeeid, foremployeeid)) },
  update(partnerplandata) { return request.put('/partnerplan/', partnerplandata) },
  updatecomments(partnerplandata) { return request.put('/partnerplan/updatecomments', partnerplandata) },
  getplans(planyear, employeeid) { return request.post(`/documents/getpartnerplans/${planyear}`, requestBody(employeeid)) },
  getratings(libraryName, folderName) { return request.get(`/documents/gettemplate/${libraryName}/${folderName}`) },
  createplansforfy(employeeid, planyear) { return request.post(`/partnerplan/openplans/${planyear}`, requestBody(employeeid)) },
  updateplanstatus(employeeid, planyear, planstatus, modifiedby) { return request.post(`/partnerplan/updateplanstatus/${planyear}/${planstatus}/${modifiedby}`, requestBody(employeeid)) },
  updateplanstatuses(employeeid, planyear, planstatus, modifiedby) { return request.post(`/partnerplan/updateplanstatuses/${planyear}/${planstatus}/${modifiedby}`, requestBody(employeeid)) },
  getpartnerplanreminderemail(employeeid) { return request.post('/partnerplan/all/partnerreminderemail', requestBody(employeeid)) },
  sendpartnerplanreminderemail(employeeid, selectedemployeeids, planstatus, runtest) {
    return request.post(`/partnerplan/all/${planstatus}/sendpartnerreminderemail/${runtest}`, requestBody(employeeid, selectedemployeeids))
  },
}

const PartnerPlanAdmin = {
  getcurrentoverviewpage(employeeid) { return request.post('/partnerplan/overview/active', requestBody(employeeid)) },
  listoverviewpages(employeeid) { return request.post('/partnerplan/overview/lists', requestBody(employeeid)) },
  updateoverviewpage(overviewdata) { return request.post('/partnerplan/overview/update', overviewdata) },
  setoverviewpageactive(employeeid, planstage) { return request.post(`/partnerplan/overview/active/${planstage}`, requestBody(employeeid)) },
  listuploadlog(employeeid, numDays) { return request.post(`/partnerplan/uploadlog/lists/${numDays}`, requestBody(employeeid)) },
  listratingvisibility(employeeid, year) { return request.post(`/partnerplanratingvisibility/lists/${year}`, requestBody(employeeid)) },
  updateratingvisibility(ratingvisibilitydata) { return request.put('/partnerplanratingvisibility/', ratingvisibilitydata) },
}

const PartnerChildren = {
  list(employeeid) { return request.post('/partnerchildren/lists', requestBody(employeeid)) },
  details(id, employeeid) { return request.post(`/partnerchildren/get/${id}`, requestBody(employeeid)) },
  create(data) { return request.post('/partnerchildren', data) },
  update(data) { return request.put('/partnerchildren', data) },
  delete(data) { return request.post(`/partnerchildren/delete/${data.id}`, requestBody(data.employee_id)) },
}

const Template = {
  gettemplates(libraryName, folderName) { return request.get(`/documents/gettemplate/${libraryName}/${folderName}`) },
}

const FileUploader = {
  // uploadchunksplan(documenttype, username, employeeid, planyear, planstatus, blemail, cilemail, cpemail, mlemail) {
  //   return `${axios.defaults.baseURL}/documents/uploadpartnerplans/chunks/${planyear}/${username}/${employeeid}/${documenttype}/${planstatus}/${getConfig().PLAN_LIBRARY}`
  // },
  uploadplan() {
    return `${axios.defaults.baseURL}/documents/uploadpartnerplans`
  },
  uploadbeneficiary() {
    return `${axios.defaults.baseURL}/beneficiary/upload/${getConfig().BENEFICIARY_UPLOAD}`
  },
  uploadhierarchy() {
    return `${axios.defaults.baseURL}/partnerhierarchy/upload/${getConfig().HIERARCHY_UPLOAD}`
  },  
  uploadplanratings() {
    return `${axios.defaults.baseURL}/documents/uploadplanratings`
  },
  uploadtemplate(libraryName, folderName) {
    return `${axios.defaults.baseURL}/documents/uploadtemplate/${libraryName}/${folderName}/false`
  },
  deleteplan(itemid, employeeid, year) { return request.post(`/documents/deletepartnerplan/${itemid}/${year}/${getConfig().PLAN_LIBRARY}`, requestBody(employeeid)) },
  deleterating(itemid, libraryName, folderName) { return request.del(`/documents/deletetemplate/${itemid}/${libraryName}/${folderName}`) },
  deletetemplate(itemid, libraryName, folderName) { return request.del(`/documents/deletetemplate/${itemid}/${libraryName}/${folderName}`) },
}

const MDM = {
  getMdmUsers(query, employeeid, onlyPartners = false, onlyActiveEmployees = false) {
    const boomi = {
      query,
      employee_id: employeeid,
      onlyPartners,
      onlyActiveEmployees,
    }
    return request.post('/MDM/getuserfrommdm', boomi)
  },
}

const ManagePermissions = {
  addAdminUser(data, employeeid) {
    const newData = merge({}, data, {
      employee_id: employeeid,
    })
    return request.post('/ManagePermissions/PermissionsData', newData)
  },
  editAdminUser(data, employeeid) {
    const newData = merge({}, data, {
      employee_id: employeeid,
    })
    return request.put('/ManagePermissions/PermissionsData', newData)
  },
  getAdminUsers(employeeid) {
    return request.post('/ManagePermissions/getalladminuserpermissions', requestBody(employeeid))
  },
  getAdminRoles(employeeid) {
    return request.post('/ManagePermissions/getadminroles', requestBody(employeeid))
  },
}

const PartnerHierarchy = {
  editPartnerHierarchy(data) {
    return request.put('/PartnerHierarchy/updatehierarchy', data)
  },
  getPartnerHierarchy(employeeid, selectedPartnerId) {
    return request.post('/PartnerHierarchy/getpartnerhierarchy', requestBody(employeeid, selectedPartnerId))
  },
  syncPartnerHierarchyDBUpdate(employeeid) {
    return request.post('/PartnerHierarchy/synchierarchydbupdate', requestBody(employeeid))
  },
  getuploads() { return request.get(`/documents/gethierarchyuploads/${getConfig().HIERARCHY_UPLOAD}`) },
}

const FeatureToggle = {
  list(employeeid) { return request.post('/featuretoggles/lists', requestBody(employeeid)) },
  update(data) {
    return request.put(`/featuretoggles/update/${data.feature_id}`, data)
  },
}

export const agent = {
  PersonalInfo,
  Beneficiary,
  AccountsAndLoans,
  BankDetail,
  PartnerChildren,
  PartnerSideBar,
  PartnerPlans,
  PartnerPlanAdmin,
  Documents,
  Template,
  FileUploader,
  UploadLogs,
  // FileHelper,
  Reports,
  PageHTMLContents,
  ManagePermissions,
  MDM,
  PartnerHierarchy,
  FeatureToggle,
}
// -----------------
// API common calls - Shirley's sample code
// -----------------
export const getRequest = (api) => {
  const url = `${api}`
  return axios.get(url).then(({ data }) => {
    return {
      data: data.result,
    }
  })
}

export const postRequest = (api, data) => {
  const url = `${api}`
  return axios.post(url, data).then(({ data: fetchedData }) => {
    return {
      data: fetchedData.result,
    }
  })
}
/* export const postRequestBinaryFile = (api, data) => {
  const url = `${api}`
  return axios
    .post(url, data, { responseType: 'arraybuffer' })
    .then(({ data: responseData, request }) => {
      const filenamePattern = /attachment; filename=([^;]*)/
      const regexResult = filenamePattern.exec(
        request.getResponseHeader('Content-Disposition'),
      )
      const filename = regexResult.length > 1 && regexResult[1]
      return {
        data: responseData,
        filename,
      }
    })
} */

export const getRequestUrl = api => `${axios.defaults.baseURL}${api}`

// -----------------
// API NOT IN USED
// -----------------
// Entities
const USER = 'user'
const getThings = (entityName) => {
  return (loginname, empid, params) => {
    return axios.get(`/${entityName}/${loginname}/${empid}`, { params })
  }
}
const getThing = (entityName) => {
  return (entityId, params) => {
    return axios.get(`/${entityName}/${entityId}`, { params })
  }
}
const createThing = (entityName) => {
  return (entity) => {
    return axios.post(`/${entityName}`, entity)
  }
}
const updateThing = (entityName) => {
  return (entity) => {
    return axios.put(`/${entityName}/${entity.id}`, entity)
  }
}
const updateThingPartially = (entityName) => {
  return (partOfEntity) => {
    return axios.patch(`/${entityName}/${partOfEntity.id}`, partOfEntity)
  }
}
const deleteThing = (entityName) => {
  return (entityId) => {
    return axios.delete(`/${entityName}/${entityId}`)
  }
}
export const updateSessionObject = () => {
  sessionStorage.setItem('IsAuthenticated', 'false')
  sessionStorage.setItem('SharepointToken', 'false')
  sessionStorage.setItem('RedirectUrl', window.location)
  window.location = window.location.origin
}

// -----------------
// Uers APIs
// -----------------
export const fetchUsers = getThings(USER)
export const fetchUser = getThing(USER)
export const createUser = createThing(USER)
export const updateUser = updateThing(USER)
export const updateUserPartially = updateThingPartially(USER)
export const deleteUser = deleteThing(USER)
// Generic Error handling
// -----------------
export const parseError = (error) => {
  // console.error(`API ERROR: ${error.config.url}`, error)
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const {
      data,
      status,
      // headers,
    } = error.response
    // debugger
    const description = data
    // && data.info && data.info.message
    if (data && data.toString().trim() === 'No record found') {
      return {
        error,
        status: constants.ERROR.BAD_REQUEST,
        description: data,
      }
    }
    switch (status) {
      case 400:
        return {
          error,
          status: constants.ERROR.BAD_REQUEST,
          description,
        }
      case 401: {
        return {
          error,
          status: constants.ERROR.UNAUTHORIZED,
          description,
        }
      }
      case 403:
        return {
          error,
          status: constants.ERROR.FORBIDDEN,
          description,
        }
      case 404:
        return {
          error,
          status: constants.ERROR.NOT_FOUND,
          description,
        }
      case 503:
        return {
          error,
          status: constants.ERROR.SERVICE_UNAVAILABLE,
          description,
        }
      default:
        return {
          error,
          status: constants.ERROR.INTERNAL_SERVER,
          description,
        }
    }
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js

    if (error.message.toLowerCase() === 'network error') {
      return {
        error,
        status: constants.ERROR.SERVICE_UNAVAILABLE,
      }
    }

    return {
      error,
      status: constants.ERROR.OFFLINE,
    }
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      error,
      status: constants.ERROR.UNKNOWN,
    }
  }
}



// WEBPACK FOOTER //
// ./src/utils/api.js